/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import _ from 'underscore';
import { Promise } from 'bluebird';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@fluentui/react-components';

// import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
// import {
//   ClarityTags, useClarityTags,
//   GridSectionName,
//   CSMLSearchLocation,
//   ROOT_FOLDER_ID,
//   RealUsedFlagSuffix,
//   handleRealUsedFlag,
//   addAssetLibrarySurveyTriggerFlag,
//   AssetLibrarySurveyTriggerFlag,
// } from '@bingads-webui-campaign-react/asset-library-model';
import {
  AdsStudioLibraryPageContent, GridSectionName, AdsStudioAssetGridCell, HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { AdsStudioFoldersGridCellWithContextMenu } from './components/folders-grid-cell-with-context-menu';

import { useCreateFolderModal } from './hooks/use-create-folder-modal';
import { useDeleteFolderConfirmDialog, useBatchDeleteConfirmDialog } from './hooks/use-delete-confirm-dialog';
import { useRenameFolderModal } from './hooks/use-rename-folder-modal';
import { useSummaryResultModal } from './components/summary-result-dialog';
import { useDragAndDropConfirmDialog } from './hooks/use-drag-and-drop-confirm-dialog';

// import { AddFoldersTeachingBubble } from './add-folders-teaching-bubble';
// import { AssetLibraryAssetGridCell } from './cells/asset-grid-cell';
// import { AssetLibraryFoldersGridCellWithContextMenu } from './cells/folders-grid-cell-with-context-menu';
// import { useAssetGridCellActions } from './cells/use-asset-grid-cell-actions';
// import { CustomAddAssetsButton } from './custom-add-assets-button';
import { LibraryPageToolbar } from './components/toolbar';
import { FolderAssetType } from '../../../../ads-studio-app-shared-components/src/model';

export const LibraryPageComponent = ({
  dataService,
  navigation,
  i18n,
  dateFormat,
  ianaTimeZone,
  assetLibraryUserPreferences,
  adsStudioUserPreferences,
  assetLibraryAccountPreferences,
  reload,
  logger,
  isReadonly,
  pagePerfMarker: libraryPagePerfMarker,
  allowedAssetType,
  aid,
  cid,
  oDataConfig,
  isAdsStudioAIGCMVPEnabled,
  isAIGCRegionRestricted,
  useBatchMoveModalHook,
  notificationApi,
  renderHelpPopup,
  targetDropFolder,
  resetTargetDropFolder,
  setDataLoaded,
  permissions,
  ...rest
}) => {
  const [reloadingFolders, setReloadingFolders] = useState(false);

  const [searchLocationFilter, setSearchLocationFilter] = useState(null);

  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [batchDeleteWarningMsg, setBatchDeleteWarningMsg] = useState('');
  const singleImagePromises = useRef({});

  /* istanbul ignore next */
  const selectedItemCount = useMemo(() => (selectedAssets ? selectedAssets.length : 0)
    + (selectedFolders ? selectedFolders.length : 0), [selectedAssets, selectedFolders]);

  const createFolderModal = useCreateFolderModal(aid, i18n, logger);
  const renameFolderModal = useRenameFolderModal(i18n, logger);
  const confirmDeleteFolderDialog = useDeleteFolderConfirmDialog(i18n, logger);
  const confirmBatchDeleteDialog = useBatchDeleteConfirmDialog(i18n, logger);
  const confirmDragAndDropDialog = useDragAndDropConfirmDialog(i18n, logger);
  const batchMoveModal = useBatchMoveModalHook({
    i18n, logger, aid, cid, oDataConfig, dateFormat, notificationApi, ...rest,
  });
  const summaryResultModal = useSummaryResultModal(i18n, logger, navigation, renderHelpPopup);

  // reset selection state when changing folders
  const resetSelectionState = useCallback(() => {
    setSelectedFolders([]);
    setSelectedAssets([]);
  }, []);

  // const {
  //   onCellEditClick, onCellDetailsClick,
  // } = useAssetGridCellActions({
  //   logger, navigation, resetSelectionState, preference: assetLibraryAccountPreferences,
  // });

  const {
    assets,
    loadMoreData,
    totalAssetCount,
    createFolder,
    reloadFolders,
    navigateIntoFolder,
    deleteFolder,
    renameFolder,
    batchDelete,
    batchMove,
    currentFolderId,
    resetFolderData,
    folders,
    getImageById,
    reloadFolderPathTreeItem,
    renameAsset,
  } = dataService;

  useEffect(() => {
    if (navigation) {
      navigation.updateAssetDetailsParams({ assets });
    }
  }, [assets, navigation]);

  useEffect(() => () => {
    resetTargetDropFolder();
  }, [resetTargetDropFolder]);

  useEffect(() => {
    resetSelectionState();
  }, [currentFolderId, resetSelectionState]);

  usePageActiveTimeTracker(logger, 'Library');

  /* istanbul ignore next */
  const showNotificationError = useCallback((msg, options) => {
    const { buttonLabel, onButtonClick, autoClose = true } = options || {};
    let nid = null;
    const onClick = () => {
      /* istanbul ignore if */
      if (nid && autoClose) {
        notificationApi.dismiss(nid);
        nid = null;
      }
      onButtonClick();
    };
    /* istanbul ignore if */
    if (msg && buttonLabel && onButtonClick) {
      // eslint-disable-next-line react/no-danger
      nid = notificationApi.showWarning(<span ><span dangerouslySetInnerHTML={{ __html: msg }} />{' '}<Button as="a" style={{ textDecoration: 'underline', padding: 0, minWidth: 0 }} appearance="transparent" onClick={onClick}>{buttonLabel}</Button></span>);
    } else if (msg) {
      notificationApi.showWarning(msg);
    }
  }, [notificationApi]);

  // useClarityTags([ClarityTags.LaunchedAssetLibrary]);

  // reset selection when current folder changes
  useEffect(() => {
    resetSelectionState();
  }, [currentFolderId, resetSelectionState]);

  useEffect(() => {
    /* istanbul ignore if */
    if (targetDropFolder && targetDropFolder.newTargetFolder) {
      confirmDragAndDropDialog.open({ targetFolder: targetDropFolder.folder });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDropFolder]);

  const onCreateFolderClick = useCallback(() => {
    // create folder
    createFolderModal.open();
  }, [createFolderModal]);

  const renderTilesGridCell = useCallback(({
    item, index, searchText, sectionName, selected, selectItem,
  }) => {
    const onSelectCell = () => {
      selectItem(!selected);
    };

    const onDrag = () => {
      if (!selected) {
        selectItem(true);
      }
    };

    // render default folder cells
    if (sectionName === GridSectionName.Folder) {
      const currentListItem = document.getElementById(`list-item-${index}`);

      const onFolderClick = () => {
        const { folderId } = item || {};
        if (folderId) {
          resetSelectionState();
          navigateIntoFolder({ id: folderId });
          logger.logTrace('Navigate into folder in asset library', { folderId });
        }
      };

      const onSelectFolder = () => {
        onSelectCell();
        if (currentListItem) {
          currentListItem.focus();
        }
      };

      const onSelectMenuItemClick = () => {
        setSelectedFolders([item]);
        selectItem(true);
        if (currentListItem) {
          currentListItem.focus();
        }
      };

      const onRenameMenuItemClick = (renameItem) => {
      // handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.App);
        renameFolderModal.open(renameItem);
      };

      const onDeleteMenuItemClick = (deleteItem) => {
      // handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.App);
        confirmDeleteFolderDialog.open(deleteItem);
      };

      const onDrop = () => {
        confirmDragAndDropDialog.open({ targetFolder: item });
      };

      const onViewPerformanceClick = (subFolder) => {
        const { folderId, folderName } = subFolder || {};
        logger.logTrace(`Clicked view performance context menu item, folderId: ${folderId}, folderName: ${folderName}`);
        if (folderId) {
          resetSelectionState();
          resetFolderData();
          navigateIntoFolder({ id: folderId });
          navigation.gotoPerformancePage();
        }
      };

      const FoldersGridCell = AdsStudioFoldersGridCellWithContextMenu;

      return (
        <div>
          <FoldersGridCell
            item={item}
            highlightedText={searchText}
            onFolderClick={onFolderClick}
            onSelect={onSelectFolder}
            i18n={i18n}
            selectable={selectedItemCount > 0}
            selected={selected}
            allowedAssetType={allowedAssetType}
            onSelectMenuItemClick={onSelectMenuItemClick}
            onOpenMenuItemClick={onFolderClick}
            onRenameMenuItemClick={onRenameMenuItemClick}
            onDeleteMenuItemClick={onDeleteMenuItemClick}
            onViewPerformanceClick={onViewPerformanceClick}
            isReadonly={isReadonly}
            selectedItemCount={selectedItemCount}
            draggable
            onDrag={onDrag}
            droppable={!selected}
            onDrop={onDrop}
          />
        </div>
      );
    }

    const onCellDetailsClick = (assetItem) => {
      const { id, folderAssetType } = assetItem || {};
      resetSelectionState();
      navigation.gotoAssetDetails({
        assetId: id, assets, totalAssetCount, folderAssetType, loadMoreData, renameAsset,
      });
    };

    const onCellEditClick = (assetItem) => {
      const { id } = assetItem || {};
      /* istanbul ignore if */
      if (assetItem && assetItem.folderAssetType === FolderAssetType.Video && assetItem.id && assetItem.metaData) {
        if (logger && logger.logTrace) {
          logger.logTrace('Video Details: edit video', { assetId: id });
        }
        navigation.gotoEditVideoPage({ videoId: item.id });
      } else if (assetItem && assetItem.folderAssetType === FolderAssetType.Image) {
        if (logger && logger.logTrace) {
          logger.logTrace('Image Details: edit image', { assetId: id });
        }
        navigation.gotoEditImagePage({ imageId: id, folderId: currentFolderId });
      }
    };

    return (
      <AdsStudioAssetGridCell
        item={item}
        onDetailsClick={onCellDetailsClick}
        onEditClick={onCellEditClick}
        index={index}
        i18n={i18n}
        highlightedText={searchText}
        logger={logger}
        selectable
        selected={selected}
        onSelect={onSelectCell}
        draggable
        onDrag={onDrag}
        selectedItemCount={selectedItemCount}
        isMultiSelecting={selectedItemCount > 0}
        permissions={permissions}
      />
    );
  }, [allowedAssetType, assets, confirmDeleteFolderDialog, confirmDragAndDropDialog, currentFolderId, i18n, isReadonly, loadMoreData, logger, navigateIntoFolder, navigation, renameAsset, renameFolderModal, resetFolderData, resetSelectionState, selectedItemCount, totalAssetCount, permissions]);

  const renderToolbar = useCallback(() => {
    const onDeleteClick = (item) => {
      // handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.App);
      confirmBatchDeleteDialog.open(item);
    };

    const onMoveToClick = (item) => {
      // handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.App);
      batchMoveModal.open(item);
    };

    return (
      selectedItemCount > 0 ?
        <div className="library-page-toolbar">
          <LibraryPageToolbar
            isReadonly={isReadonly}
            i18n={i18n}
            onDelete={onDeleteClick}
            onMoveTo={onMoveToClick}
            onClear={resetSelectionState}
            selectedCount={selectedItemCount}
          />
        </div> : null
    );
  }, [selectedItemCount, isReadonly, i18n, resetSelectionState, confirmBatchDeleteDialog, batchMoveModal]);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load assets. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  const onSelectionChange = useCallback(({
    sectionName, selectedItems, item, selected,
  }) => {
    if (sectionName === GridSectionName.Folder) {
      setSelectedFolders([...selectedItems]);
    } else if (sectionName === GridSectionName.Asset) {
      if (selected && getImageById && item && item.metadata) {
        // preload image data
        const {
          id = '',
        } = item;
        singleImagePromises.current[id] = getImageById({ imageId: id, useCache: false });
      }
      setSelectedAssets([...selectedItems]);
    }
  }, [getImageById]);

  useEffect(() => {
    if (getImageById && selectedAssets.length > 0) {
      const checkSelectedAssetsPromise = selectedAssets.map((asset) => {
        if (asset && asset.metadata) {
          const {
            id = '',
          } = asset;
          const previousPromise = singleImagePromises.current[id];
          return previousPromise || getImageById({ imageId: id, useCache: false });
        }
        return Promise.resolve({});
      });

      Promise.all(checkSelectedAssetsPromise).then((assetsList) => {
        const hasVideoImage = assetsList.some(asset => asset && asset.isSceneKeyFrame);
        if (hasVideoImage) {
          setBatchDeleteWarningMsg(i18n.getString(_TL_('Deleted images will still serve in any video ads that currently use it and cannot be edited.')));
        } else {
          setBatchDeleteWarningMsg('');
        }
      });
    }
  }, [getImageById, i18n, selectedAssets]);

  const sectionSelectedItems = useMemo(() => ({
    [GridSectionName.Folder]: selectedFolders,
    [GridSectionName.Asset]: selectedAssets,
  }), [selectedAssets, selectedFolders]);

  const callAsyncApi = useCallback(asyncApi => (...params) => {
    setReloadingFolders(true);
    return asyncApi(...params).then((result) => {
      // reload grids
      reloadFolders().finally(() => {
        setReloadingFolders(false);
      });
      const destFolderId = params[0] && params[0].destFolderId;
      const reloadFolderPathTreeFolderIdArr = [currentFolderId];
      if (destFolderId) {
        reloadFolderPathTreeFolderIdArr.push(destFolderId);
      }
      // reload folder path tree
      reloadFolderPathTreeItem(reloadFolderPathTreeFolderIdArr);
      return result;
    }).tapCatch(() => {
      setReloadingFolders(false);
    }).finally(() => {
      resetSelectionState();
    });
  }, [currentFolderId, reloadFolderPathTreeItem, reloadFolders, resetSelectionState]);

  const createFolderAsync = callAsyncApi(createFolder);
  const renameFolderAsync = callAsyncApi(renameFolder);
  const deleteFolderAsync = callAsyncApi(deleteFolder);
  const batchDeleteAsync = callAsyncApi(({ onItemDeleted }) => batchDelete({ folders: selectedFolders, assets: selectedAssets, onItemDeleted }));
  const batchMoveAsync = callAsyncApi(({ destFolderId }) => batchMove({ destFolderId, folders: selectedFolders, assets: selectedAssets }));

  const onCreateFolderSuccess = (folderName) => {
    notificationApi.showInfo(i18n.getString(_TL_('Your folder <b>{{name}}</b> was successfully created.'), { name: folderName }));
  };

  const onRenameFolderSuccess = ({ folderName, newFolderName }) => {
    notificationApi.showInfo(i18n.getString(_TL_('The <b>{{folderName}}</b> folder was successfully renamed to <b>{{newFolderName}}</b>.'), { folderName, newFolderName }));
  };

  const onDeleteFolderSuccess = ({ deletedItems, folderName }) => {
    if (_.find(deletedItems, itm => !!itm.err)) {
      showNotificationError(i18n.getString(_TL_("The <b>{{folderName}}</b> folder couldn't be deleted due to errors. To review the errors, select "), { folderName }), {
        buttonLabel: i18n.getString(_TL_('View details')),
        onButtonClick: () => {
          logger.logTrace('View deleting folder details result');
          summaryResultModal.openFolderDeleteSummaryResultModal(deletedItems);
        },
      });
    } else {
      notificationApi.showInfo(i18n.getString(_TL_('The <b>{{folderName}}</b> folder was successfully deleted.'), { folderName }));
    }
  };

  const onBatchDeleteSuccess = ({ deletedItems, folderCount, assetCount }) => {
    if (_.find(deletedItems, itm => !!itm.err)) {
      showNotificationError(i18n.getString(_TL_("One or more items couldn't be deleted due to errors. To review the errors, select ")), {
        buttonLabel: i18n.getString(_TL_('View details')),
        onButtonClick: () => {
          logger.logTrace('View batch deleting details result');
          summaryResultModal.openBatchDeleteSummaryResultModal(deletedItems);
        },
      });
    } else {
      notificationApi.showInfo(i18n.getString(
        _TL_('({{folderCount}} folders, {{assetCount}} assets) were successfully deleted.'),
        { folderCount, assetCount }
      ));
    }
  };

  const onBatchMoveSuccess = ({ assetsSummary, foldersSummary, hasError }) => {
    if (hasError) {
      showNotificationError(i18n.getString(_TL_("One or more items couldn't be moved due to errors. To review the errors, select ")), {
        buttonLabel: i18n.getString(_TL_('View details')),
        onButtonClick: () => {
          logger.logTrace('View batch moving details result');
          summaryResultModal.openBatchMoveSummaryResultModal({ assetsSummary, foldersSummary });
        },
      });
    } else {
      notificationApi.showInfo(i18n.getString(
        _TL_('All items ({{folderCount}} folders, {{assetCount}} assets) were successfully moved. '),
        { folderCount: foldersSummary.length, assetCount: assetsSummary.length }
      ));
    }
  };

  return (
    <div style={{ margin: '0 21px' }}>
      <AdsStudioLibraryPageContent
        dataService={dataService}
        i18n={i18n}
        aid={aid}
        cid={cid}
        oDataConfig={oDataConfig}
        dropdownWidth="auto"
        dateFormat={dateFormat}
        ianaTimeZone={ianaTimeZone}
        assetLibraryUserPreferences={assetLibraryUserPreferences}
        adsStudioUserPreferences={adsStudioUserPreferences}
        assetLibraryAccountPreferences={assetLibraryAccountPreferences}
        reload={reload}
        logger={logger}
        renderGridCell={renderTilesGridCell}
        renderToolbar={renderToolbar}
        onFailedToLoadData={onFailedToLoadData}
        dataLoaded={setDataLoaded}
        allowedAssetType={allowedAssetType}
        reloadingFolders={reloadingFolders}
        multiSelectionEnabled
        onSelectionChange={onSelectionChange}
        sectionSelectedItems={sectionSelectedItems}
        searchLocationFilterState={searchLocationFilter}
        setSearchLocationFilterState={setSearchLocationFilter}
        perfMarker={libraryPagePerfMarker}
        // realUsedFlagSuffix={RealUsedFlagSuffix.App}
        isAdsStudioAIGCMVPEnabled={isAdsStudioAIGCMVPEnabled}
        isAIGCRegionRestricted={isAIGCRegionRestricted}
        notificationApi={notificationApi}
        displayFolderPath
        onCreateFolderClick={onCreateFolderClick}
        navigation={navigation}
        permissions={permissions}
      />
      <HiddenDivForAnnounce toAnnounce={i18n.getString(_TL_('{{selectedItemCount}} items selected'), { selectedItemCount })} />
      {createFolderModal.render(createFolderAsync, onCreateFolderSuccess, showNotificationError)}
      {renameFolderModal.render(renameFolderAsync, onRenameFolderSuccess, showNotificationError)}
      {confirmBatchDeleteDialog.render(batchDeleteAsync, onBatchDeleteSuccess, showNotificationError, batchDeleteWarningMsg)}
      {confirmDeleteFolderDialog.render(deleteFolderAsync, onDeleteFolderSuccess, showNotificationError)}
      {confirmDragAndDropDialog.render(batchMoveAsync, onBatchMoveSuccess, showNotificationError)}
      {batchMoveModal.render(false, currentFolderId, batchMoveAsync, onBatchMoveSuccess, showNotificationError)}
      {summaryResultModal.render()}
    </div>
  );
};

LibraryPageComponent.propTypes = {
  dataService: PropTypes.shape({
    assets: PropTypes.arrayOf(PropTypes.shape({})),
    folders: PropTypes.arrayOf(PropTypes.shape({})),
    totalAssetCount: PropTypes.number,
    imageCroppingTasks: PropTypes.shape({}),
    loadData: PropTypes.func,
    loadMoreData: PropTypes.func,
    createFolder: PropTypes.func,
    reloadFolders: PropTypes.func,
    navigateIntoFolder: PropTypes.func,
    deleteFolder: PropTypes.func,
    renameFolder: PropTypes.func,
    batchDelete: PropTypes.func,
    batchMove: PropTypes.func,
    resetFolderData: PropTypes.func,
    getImageById: PropTypes.func,
    currentFolderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    reloadFolderPathTreeItem: PropTypes.func,
    renameAsset: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoAddImage: PropTypes.func.isRequired,
    gotoEditImage: PropTypes.func.isRequired,
    gotoItemDetail: PropTypes.func.isRequired,
    gotoVideoDetail: PropTypes.func,
    gotoPerformancePage: PropTypes.func.isRequired,
    // goBack: PropTypes.func.isRequired,
  }).isRequired,
  // lcid: PropTypes.number.isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getToday: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  aid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({
    sortBy: PropTypes.string,
    setSortBy: PropTypes.func,
  }),
  assetLibraryAccountPreferences: PropTypes.shape({}),
  isReadonly: PropTypes.bool,
  reload: PropTypes.shape({}), // if changed, force to reload
  perfMarker: PropTypes.shape({
    done: PropTypes.func,
  }).isRequired,
  allowedAssetType: PropTypes.string,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isAdsStudioAIGCMVPEnabled: PropTypes.bool,
  useBatchMoveModalHook: PropTypes.func.isRequired,
  renderHelpPopup: PropTypes.func.isRequired,
  notificationApi: PropTypes.shape({
    showInfo: PropTypes.func,
    showWarning: PropTypes.func,
    dismiss: PropTypes.func,
  }).isRequired,
  targetDropFolder: PropTypes.shape({}),
  isAIGCRegionRestricted: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
};

LibraryPageComponent.defaultProps = {
  assetLibraryUserPreferences: null,
  assetLibraryAccountPreferences: null,
  isReadonly: false,
  reload: null,
  allowedAssetType: null,
  isAssetLibraryAIGCMVPEnabled: false,
  isAdsStudioAIGCMVPEnabled: false,
  targetDropFolder: null,
  isAIGCRegionRestricted: false,
  permissions: { dynamic: {} },
};
